// Inhalte der Broker-Export-Anleitungen auf /imports. Getrennt von der
// Darstellung (ExportGuideSection), damit Text-Korrektionen keine
// Komponenten-Änderung sind. Pflicht-Spalten der IBKR-Anleitung spiegeln
// den Parser-Vertrag in backend/app/services/broker_imports/ibkr.py.

export interface GuideScreenshot {
  file: string
  caption: string
}

export interface GuideStep {
  title: string
  text: string
  screenshot?: GuideScreenshot
}

export interface ExportGuide {
  brokerType: 'ibkr' | 'consors' | 'scalable'
  title: string
  status: 'ready' | 'planned'
  intro: string
  facts: { label: string; value: string }[]
  steps: GuideStep[]
  outro?: string
}

const COLLECT_HINT =
  'Empfehlung: einmal pro Monat exportieren und ablegen, damit beim späteren Import ' +
  'keine Lücken entstehen.'

export const EXPORT_GUIDES: ExportGuide[] = [
  {
    brokerType: 'ibkr',
    title: 'Interactive Brokers (Flex Query)',
    status: 'ready',
    intro:
      'Sextant liest eine Activity-Flex-Query als CSV. Die Query wird einmal konfiguriert ' +
      'und liefert danach mit zwei Klicks den aktuellen Export. Die genauen ' +
      'UI-Beschriftungen sind noch nicht gegen die echte IBKR-Oberfläche verifiziert und ' +
      'können leicht abweichen.',
    facts: [
      { label: 'Format', value: 'CSV, UTF-8, Komma-Separator' },
      { label: 'Datum', value: 'DD/MM/YYYY' },
      { label: 'Dezimaltrenner', value: 'Punkt' },
      { label: 'Max. Größe', value: '10 MB' },
    ],
    steps: [
      {
        title: 'Flex Query anlegen',
        text:
          'Im Client Portal anmelden, dann Performance & Berichte → Flex-Queries öffnen ' +
          'und bei „Activity-Flex-Query" auf „+" klicken. Name z. B. „Sextant-Import".',
        screenshot: {
          file: 'imports_export_ibkr_flexquery_create.png',
          caption: 'Flex-Queries-Übersicht mit „+"-Button für eine neue Activity-Flex-Query',
        },
      },
      {
        title: 'Section „Trades" aktivieren',
        text:
          'Section „Trades" wählen, Option „Executions". Pflicht-Felder: TradeID, ' +
          'TradeDate, ISIN, Symbol, CurrencyPrimary, Buy/Sell, Quantity, TradePrice, ' +
          'IBCommission, NetCash. Empfohlen zusätzlich: Description, SubCategory, ' +
          'TradeMoney (ermöglicht den Plausibilitäts-Check beim Import).',
        screenshot: {
          file: 'imports_export_ibkr_trades.png',
          caption: 'Feld-Auswahl der Trades-Section',
        },
      },
      {
        title: 'Section „Open Positions" aktivieren',
        text:
          'Option „Summary". Pflicht-Felder: ISIN, Symbol, CurrencyPrimary, Quantity. ' +
          'Empfohlen zusätzlich: Description, SubCategory.',
      },
      {
        title: 'Section „Cash Report" aktivieren',
        text:
          'Pflicht-Felder: CurrencyPrimary, FromDate, ToDate, StartingCash, Deposits, ' +
          'Withdrawals.',
      },
      {
        title: 'Format-Einstellungen prüfen',
        text:
          'In der General Configuration der Query: Format „CSV", Datumsformat ' +
          '„dd/MM/yyyy", „Include column headers" aktiv und „Include section code and ' +
          'line descriptor" aktiv — ohne diese Optionen kann Sextant die Datei nicht ' +
          'zuordnen.',
        screenshot: {
          file: 'imports_export_ibkr_delivery.png',
          caption: 'General Configuration mit CSV-Format und Section-Codes',
        },
      },
      {
        title: 'Ausführen & herunterladen',
        text:
          'In der Flex-Queries-Liste die Query ausführen (Pfeil-Icon), Zeitraum wählen ' +
          '(z. B. seit dem letzten Import), CSV herunterladen und oben unter „Quelle ' +
          'wählen" hochladen.',
        screenshot: {
          file: 'imports_export_ibkr_run.png',
          caption: 'Query ausführen und Zeitraum wählen',
        },
      },
    ],
  },
  {
    brokerType: 'consors',
    title: 'Consorsbank (Umsatz-CSV)',
    status: 'planned',
    intro:
      'Für Consorsbank gibt es noch keinen Importer. Sammle die Exporte trotzdem schon ' +
      'jetzt — der Import kommt in einer späteren Phase und verarbeitet die Dateien ' +
      'rückwirkend.',
    facts: [{ label: 'Format', value: 'CSV, iso-8859-1, Semikolon-Separator' }],
    steps: [
      {
        title: 'Umsätze öffnen',
        text:
          'Im Online-Banking anmelden, Depot auswählen und die Umsatz-Übersicht öffnen ' +
          '(Pfad noch nicht gegen die echte Consors-Oberfläche verifiziert).',
        screenshot: {
          file: 'imports_export_consors_umsaetze.png',
          caption: 'Umsatz-Übersicht im Consorsbank-Online-Banking',
        },
      },
      {
        title: 'Zeitraum wählen & exportieren',
        text:
          'Gewünschten Zeitraum einstellen und den CSV-Export starten. Datei mit Datum ' +
          'im Namen ablegen, z. B. consors_2026-07.csv.',
      },
    ],
    outro: COLLECT_HINT,
  },
  {
    brokerType: 'scalable',
    title: 'Scalable Capital (Transaktions-CSV)',
    status: 'planned',
    intro:
      'Für Scalable Capital gibt es noch keinen Importer. Sammle die Exporte trotzdem ' +
      'schon jetzt — der Import kommt in einer späteren Phase und verarbeitet die ' +
      'Dateien rückwirkend.',
    facts: [{ label: 'Format', value: 'CSV, vermutlich UTF-8 (unbestätigt)' }],
    steps: [
      {
        title: 'Reports öffnen',
        text:
          'In der Scalable-App oder im Web anmelden und den Bereich Konto → ' +
          'Reports/Dokumente öffnen (Pfad noch nicht gegen die echte ' +
          'Scalable-Oberfläche verifiziert).',
        screenshot: {
          file: 'imports_export_scalable_reports.png',
          caption: 'Reports-Bereich bei Scalable Capital',
        },
      },
      {
        title: 'Transaktionen exportieren',
        text:
          'CSV-Export der Transaktionen herunterladen und mit Datum im Namen ablegen, ' +
          'z. B. scalable_2026-07.csv.',
      },
    ],
    outro: COLLECT_HINT,
  },
]
